// The Toolkit's unified item model. Merges two sources:
//  - "prompt" items: the therapist's prompt techniques from GET /prompts
//  - "experiential" items: the static catalog in features/technique-library
// EXP_TOOL_MAP bridges catalog codes to the experiential tool registry keys.
// Invariant (enforced by catalog.test.ts): every non-wip catalog entry maps
// to a TechniqueKey — an unmapped entry must be marked wip in the catalog.
import { LIBRARY, TechniqueEntry } from "../technique-library/catalog";
import { TechniqueKey } from "../experiential/types";

export type ToolkitItem =
  | {
      id: string; // "prompt:<templateId>"
      kind: "prompt";
      templateId: string;
      title: string;
      description: string;
    }
  | {
      id: string; // "exp:<code>"
      kind: "experiential";
      code: string;
      techniqueKey?: TechniqueKey; // absent only for wip entries
      modality: TechniqueEntry["modality"];
      wip: boolean;
      title: string; // English fallback; UI prefers i18n keys via techniqueKey
    };

export type PromptTechnique = {
  id: string;
  title: string;
  description?: string;
  kind?: string;
};

export const EXP_TOOL_MAP: Record<string, TechniqueKey> = {
  "EXP-01": "schema_chair",
  "EXP-02": "imagery_rescript",
  "EXP-04": "spatial_modes",
  "EXP-05": "defusion_voice",
  "EXP-06": "safe_place",
  "EXP-07": "empty_chair",
  "EXP-08": "parts_dialogue",
  "EXP-09": "bus_passengers",
  "EXP-10": "compassionate_letter",
  "EXP-11": "needed_words",
  "EXP-12": "felt_sense",
  "EXP-13": "safe_container",
  "EXP-14": "metaphor_deck",
  "EXP-15": "distant_screen",
  "EXP-16": "compassionate_other",
  "EXP-17": "loop_tape",
  "EXP-18": "tape_forward",
  "EXP-19": "urge_surfing",
  "EXP-20": "couples_cycle",
  "EXP-21": "brain_cast",
  "EXP-22": "playback_theatre",
  "EXP-23": "tale_story",
  "EXP-24": "metaphor_induction",
  "EXP-25": "sleep_story",
  "EXP-26": "miracle_question",
  "EXP-27": "worry_monster",
  "EXP-28": "brave_rehearsal",
  "EXP-29": "nightmare_rescript",
  "EXP-30": "pendulation",
};

export function experientialItems(): ToolkitItem[] {
  return LIBRARY.map((e) => ({
    id: `exp:${e.code}`,
    kind: "experiential" as const,
    code: e.code,
    techniqueKey: EXP_TOOL_MAP[e.code],
    modality: e.modality,
    wip: Boolean(e.wip),
    title: e.title,
  }));
}

export function promptToItem(p: PromptTechnique): ToolkitItem {
  return {
    id: `prompt:${p.id}`,
    kind: "prompt",
    templateId: p.id,
    title: p.title,
    description: p.description || "",
  };
}
